<template>
  <!-- required for 404 handling -->
  <div v-if="errorCaused"></div>
  <div v-else-if="story" v-show="isReleased">
    <div class="relative">
      <div v-if="image" class="max-h-[600px] overflow-hidden">
        <CmsImage :blok="image" :alt="altText" />
      </div>
      <div class="relative">
        <SectionBackground class="hidden bg-gold-40 md:block" />
        <div
          v-if="!isMobile && generalTags && generalTags.length"
          class="flex justify-center gap-4 py-7 text-lg"
        >
          <TagList :tags="generalTags" placement="header" />
        </div>
        <section class="relative w-full pb-4 md:pb-16">
          <div
            class="container relative max-w-6xl bg-white p-4 shadow-smooth md:px-16 md:py-10"
          >
            <!-- HINT: Make sure there is no extra whitespace within h1.
            Note whitespace-pre-line class that will cause all whitespace
            to be displayed as is, so even new lines in the code will affect design -->
            <h1 :class="headlineClass">{{ headline }}</h1>
            <div class="flex justify-center gap-4">
              <TagList :tags="articleTags" placement="blog article" />
            </div>
            <div>
              <NuxtLazyHydrate
                v-for="(content, idx) in story.content.content"
                :key="content._uid"
                when-visible
              >
                <component
                  :is="content.component"
                  :class="{
                    'md:px-28': isContentImage(content.component, idx),
                  }"
                  :blok="content"
                  :alt="altText"
                />
              </NuxtLazyHydrate>
            </div>
            <div
              v-if="story.content.special_content"
              class="relative mt-8 py-8"
              :class="{
                '!mt-24': story.content.special_content_headline,
              }"
            >
              <SectionBackground class="bg-gold-20 shadow-smooth" />
              <div
                v-if="story.content.special_content_headline"
                class="absolute left-1/2 top-0 w-full -translate-x-1/2 translate-y-[-60%] overflow-hidden truncate text-center font-handwritten text-[58px] md:text-[80px]"
              >
                {{ story.content.special_content_headline }}
              </div>
              <NuxtLazyHydrate
                v-for="content in story.content.special_content"
                :key="content._uid"
                when-visible
              >
                <div class="relative">
                  <component
                    :is="content.component"
                    :blok="content"
                    :alt="altText"
                  />
                </div>
              </NuxtLazyHydrate>
              <div
                v-if="showCta"
                class="relative mb-8 mt-12 flex justify-center"
              >
                <LinkAsButton
                  :to="ctaLink"
                  type="secondary"
                  class="text-md !gap-2 !px-4 !py-2 !font-normal !normal-case"
                >
                  {{ ctaLabel || $t('blog.cta_label') }} &#187;
                </LinkAsButton>
              </div>
            </div>
          </div>
        </section>
        <NuxtLazyHydrate v-if="teasers" when-visible>
          <div
            class="container grid max-w-6xl grid-cols-1 md:grid-cols-2 md:gap-16 md:pb-16"
          >
            <BlogTeaser
              v-for="teaser in teasers"
              :key="teaser.id"
              :blok="teaser"
              class="border-b border-primary md:border-b-0"
              :tag-type="true"
            />
          </div>
        </NuxtLazyHydrate>
      </div>
    </div>
    <section class="relative mx-auto max-w-6xl px-6">
      <div class="my-12 flex justify-center">
        <LinkAsButton
          :to="toLocalePath({ name: 'magazine' })"
          type="secondary"
          class="text-md !gap-2 !px-4 !py-2 !font-normal !normal-case"
        >
          &laquo; {{ $t('blog.back_to_overview') }}
        </LinkAsButton>
      </div>

      <NuxtLazyHydrate when-visible>
        <MoreArticles
          :article="story"
          :blog-overview="blogOverview"
          carousel-container-class="-mx-6 md:mx-0"
        />
      </NuxtLazyHydrate>

      <NuxtLazyHydrate v-if="generalTags && generalTags.length" when-visible>
        <SectionHeadline>
          {{ $t('blog.more_topics') }}
        </SectionHeadline>
        <MoreTopics :topics="generalTags" />
      </NuxtLazyHydrate>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type {
  SbBlogArticleV2,
  SbBlogOverviewV2,
  StoryblokStory,
  Sbtag,
} from '~/cms/storyblok/types/storyblok.d.ts'
import useStoryMetaData from '~/composables/useStoryMetaData'

useRouteCache((helper) => {
  helper.setMaxAge(3600).setCacheable()
})

const i18n = useI18n()
const errorCaused = ref(false)
const route = useRoute()
const { code: currentLocaleCode } = i18n.localeProperties.value
const {
  fetching,
  data: story,
  fetchSingle,
  metaLinks,
} = useBlogArticles<SbBlogArticleV2>(`blog-article-${route.params.pathMatch}`)
const { toLocalePath } = useRouteHelpers()
const { isMobile } = useDeviceType()

const { data: blogOverview, fetchBlogOverview } =
  useBlogArticles<SbBlogOverviewV2>(`blog-overview-${route.params.pathMatch}`)

const metaDataExtensions = computed(() => ({
  link: metaLinks?.value,
}))

const metaDataOverrides = computed(() => ({
  robots: story.value?.content?.noIndex
    ? 'noindex,follow'
    : 'index,follow,max-image-preview:large',
}))

// Open Graph article tags (ECOM-2071).
const {
  ogImage: articleOgImage,
  articlePublishedTime,
  articleModifiedTime,
} = useArticleOgMeta(story)

useStoryMetaData({
  data: story,
  fetching,
  extensions: metaDataExtensions,
  overrides: metaDataOverrides,
  ogType: 'article',
  ogImage: articleOgImage,
  articlePublishedTime,
  articleModifiedTime,
})

const isStoryReleased = computed(() =>
  story.value?.content?.schedule?.length
    ? +new Date() > +new Date(story.value?.content?.schedule)
    : true,
)

const scheduleFallbackUrl = computed(
  () => story.value?.content?.scheduleFallbackUrl,
)

const isReleased = ref(false)

const scheduleGuard = () => {
  if (!story.value) {
    return
  }
  if (!fetching.value && !isStoryReleased.value && story.value) {
    if (scheduleFallbackUrl.value?.cached_url?.length) {
      if (scheduleFallbackUrl.value.linktype === 'story') {
        navigateTo(
          {
            path: `/${currentLocaleCode}/${scheduleFallbackUrl.value.cached_url}`,
          },
          { replace: true },
        )
      } else if (import.meta.client) {
        window.location.href = scheduleFallbackUrl.value.cached_url
      }
    } else {
      navigateTo(
        { path: toLocalePath({ name: 'index' }) as string },
        { replace: true },
      )
    }
  } else {
    isReleased.value = true
  }
}

await fetchOnNavigation(async () => {
  await Promise.all([fetchBlogOverview(), fetchSingle({ slug: route.path })])

  if (!story.value) {
    errorCaused.value = true
    throw createError({
      statusCode: 404,
      message: `Not found`,
    })
  } else {
    scheduleGuard()
  }
})

onMounted(() => {
  scheduleGuard()
})

const isContentImage = (componentName: string, index: number) =>
  componentName === 'AssetImage' && index !== 0

const altText = computed<string>(() => {
  const image = story.value?.content?.image?.[0]
  if (isMobile.value && image?.mobile_image?.alt) {
    return image?.mobile_image.alt
  } else {
    return image?.desktop_image?.alt || ''
  }
})

const teasers = computed(() =>
  (story.value.content.teasers?.filter((i) => typeof i !== 'string') || []).map(
    (i) => i as StoryblokStory<SbBlogArticleV2>,
  ),
)
const showCta = computed(() =>
  Boolean(story.value?.content.special_content_cta_link?.cached_url),
)
const ctaLink = computed(() =>
  toLocalePath(story.value?.content.special_content_cta_link?.cached_url || ''),
)
const ctaLabel = computed(() => story.value?.content.special_content_cta_label)
const image = computed(() => blogOverview.value?.content?.image?.[0])
const generalTags = computed(
  () =>
    blogOverview.value?.content?.tags
      ?.filter((i) => typeof i !== 'string')
      .map((i) => i as StoryblokStory<Sbtag>) || [],
)
const articleTags = computed(
  () =>
    story.value?.content?.tags
      ?.filter((i) => typeof i !== 'string')
      .map((i) => i as StoryblokStory<Sbtag>) || [],
)
const headline = computed(() => story.value?.content.title)
const headlineClass =
  'font-bold text-xl md:text-2xl text-center tracking-[0.1em] mb-8 md:my-16 break-words whitespace-pre-line'
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    de: '/magazin/[slug]',
    ch: '/magazin/[slug]',
    at: '/magazin/[slug]',
  },
})

defineOptions({ name: 'MagazinePage' })
definePageMeta({ pageType: 'static' })
</script>
